import { EmailNotificationGroupTitle, TeamMemberRole, UserType } from '#types';
import {
	Box,
	Typography,
	Chip,
	Autocomplete,
	TextField,
	Popper,
	FormControl,
	FormHelperText,
	AutocompleteRenderValue,
	UseAutocompleteProps,
} from '@mui/material';
import styles from './index.module.scss';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import rpcShared from '@rockpapercoin/rpc-shared';

export type Option = {
	label: string;
	value: string;
	userType: UserType;
	role?: TeamMemberRole;
};

export type NotificationGroupInputProps = {
	groupTitle: EmailNotificationGroupTitle;
	value: Option[];
	members: Option[];
	isInEditMode: boolean;
	onChange: ( optionOrOptions?: Option | Option[] ) => void;
	onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	validation?: { errorText?: string; emailAddresses: string[] };
	labelIsForScreenReadersOnly?: true;
	disabled?: boolean;
};

const stringToOption = ( email: string ): Option => ( {
	label: email,
	value: email,
	userType: UserType.GuestUser,
} );

const maxItems = 10;
const maxItemsMessage = 'Maximum of 10 members per notification group';

export const NotificationGroupInput: React.FC<NotificationGroupInputProps> = ( {
	value,
	members,
	groupTitle,
	isInEditMode,
	onBlur,
	onChange,
	validation,
	labelIsForScreenReadersOnly,
	disabled,
} ) => {
	const [ listValidation, setListValidation ] = useState<string | undefined>(
		undefined
	);

	useEffect( () => {
		setListValidation( validation?.errorText );
	}, [ validation?.errorText ] );

	const anchorRef = useRef<HTMLDivElement | null>( null );

	const handleSelect: NonNullable<
		UseAutocompleteProps<Option, true, true, true>['onChange']
	> = useCallback(
		( e, value ) => {
			if ( value.length > maxItems ) {
				setListValidation( maxItemsMessage );
			} else {
				setListValidation( undefined );
			}
			( e.target as HTMLInputElement ).value = '';
			// Notify the controller using this component of the selection
			onChange(
				value
					.slice( 0, maxItems )
					.map( ( val ) => ( typeof val === 'string' ? stringToOption( val ) : val ) )
			);
		},
		[ onChange ]
	);

	const handleBlur: React.FocusEventHandler<
		HTMLInputElement | HTMLTextAreaElement
	> = useCallback(
		( e ) => {
			const email = e.target.value;
			if ( email ) {
				onChange( stringToOption( email ) );
			}
			onBlur?.( e );
		},
		[ onBlur, onChange ]
	);

	const ref = useRef<HTMLDivElement>( null );

	const selectedValueAsOptions = value as AutocompleteRenderValue<
		Option,
		true,
		true
	>;
	const normalizedSelectedValues = selectedValueAsOptions.map( ( option ) =>
		typeof option === 'string' ? stringToOption( option ) : option
	);

	return (
		<Box data-cy={ `notification-group-${ groupTitle }` } ref={ anchorRef }>
			{ !labelIsForScreenReadersOnly ? (
				<Typography
					data-cy={ `notification-group-${ groupTitle }-label` }
					component='label'
					htmlFor={ ref.current?.id }
				>
					{ rpcShared.emailNotificationGroups.UserFriendlyGroupNames[ groupTitle ] }
				</Typography>
			) : null }
			{ isInEditMode ? (
				<FormControl fullWidth error={ !!listValidation } variant='standard'>
					<Autocomplete<Option, true, true, true>
						freeSolo
						autoHighlight
						multiple
						disableClearable
						blurOnSelect
						clearOnBlur
						options={ members }
						value={ value }
						disabled={ disabled }
						renderInput={ ( params ) => (
							<TextField
								{ ...params }
								inputRef={ ref }
								className={ styles.input }
								variant='standard'
								onBlur={ handleBlur }
								slotProps={ {
									...params.slotProps,
									htmlInput: {
										...params.slotProps.htmlInput,
										'data-cy': 'member-value',
										readOnly: value.length >= maxItems,
										'aria-label':
											rpcShared.emailNotificationGroups.UserFriendlyGroupNames[
												groupTitle
											],
									},
								} }
								disabled={ value.length >= maxItems }
							/>
						) }
						renderValue={ ( _selectedValues, getItemProps ) =>
							normalizedSelectedValues.map( ( option, index ) => {
								const { key, ...itemProps } = getItemProps( { index } );
								return (
									<Chip
										key={ key }
										label={ option.label }
										{ ...itemProps }
										className={
											validation?.emailAddresses.includes( option.value )
												? classNames( itemProps.className, styles.disabled )
												: itemProps.className
										}
										data-cy={ option.value }
									/>
								);
							} )
						}
						onChange={
							// keypress events aren't handled here, so this will only ever get suggestion selects or item removals
							handleSelect
						}
						isOptionEqualToValue={ ( option, value ) =>
							typeof value !== 'string' && option.value === value.value
						}
						getOptionLabel={ ( option ) =>
							typeof option === 'string' ? option : option.label
						}
						filterOptions={ ( options, { inputValue } ) =>
							value.length >= maxItems
								? []
								: options.filter(
									( option ) =>
									// don't show team members already selected
										!value.find(
											( val ) =>
												typeof option !== 'string' &&
													val.value === option.value
										) &&
											// and also show if something typed in
											( ( inputValue &&
												// or what's typed in matches an unselected team member's email
												( option.value.includes( inputValue.toLowerCase() ) ||
													// or what's typed in matches an unselected team member's name
													option.label
														.toLowerCase()
														.includes( inputValue.toLowerCase() ) ) ) ||
												!inputValue )
								  )
						}
						renderOption={ ( { key, ...props }, option ) => (
							<Typography
								component='li'
								key={ key }
								{ ...props }
								className={ classNames( props.className, styles.suggestion ) }
							>
								{ option.label }
							</Typography>
						) }
						slots={ {
							popper: Popper,
						} }
						slotProps={ {
							popper: {
								anchorEl: anchorRef.current,
								placement: 'bottom-start',
							},
						} }
					/>
					{ listValidation ? (
						<FormHelperText>{ listValidation }</FormHelperText>
					) : null }
				</FormControl>
			) : (
				<Box
					className={ styles.chipContainer }
					data-cy={ `notification-group-${ groupTitle }-chipContainer` }
				>
					{ value.length > 0
						? value.map( ( option ) => (
							<Chip
								data-cy={ `member-${ option.value }` }
								key={ option.value }
								label={ option.label }
								className={
									validation?.emailAddresses.includes( option.value )
										? classNames( styles.chip, styles.disabled )
										: styles.chip
								}
							/>
						  ) )
						: null }
				</Box>
			) }
		</Box>
	);
};
